import { FC, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import RightDrawer from '../RightDrawer';
import { BulkCreditNoteItem, BulkCreditNoteResult, useClaims } from '../../ctx/Claims.Context';
import { fEur } from '../../utils/formatNumber';

const parseInvoiceNumbers = (raw: string) =>
  [...new Set(raw.split(/[\s,;]+/).map((n) => n.trim()))].filter((n) => n !== '');

const ItemsTable: FC<{ items: BulkCreditNoteItem[]; lastColumn: 'reason' | 'creditNote' }> = ({
  items,
  lastColumn,
}) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Faktúra</TableCell>
        <TableCell>Platca</TableCell>
        <TableCell align="right">Suma</TableCell>
        <TableCell>Stav</TableCell>
        <TableCell>{lastColumn === 'reason' ? 'Poznámka' : 'Dobropis'}</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {items.map((i) => (
        <TableRow key={`bcn-${lastColumn}-${i.invoice}`}>
          <TableCell sx={{ fontFamily: 'monospace' }}>{i.invoice}</TableCell>
          <TableCell>{i.payer || '-'}</TableCell>
          <TableCell align="right">
            {i.finalPrice !== undefined ? fEur(i.finalPrice) : '-'}
          </TableCell>
          <TableCell>{i.state || '-'}</TableCell>
          <TableCell
            sx={{
              color:
                i.warning || (lastColumn === 'reason' && i.reason) ? 'warning.dark' : undefined,
              fontFamily: lastColumn === 'creditNote' ? 'monospace' : undefined,
            }}
          >
            {lastColumn === 'reason' ? i.reason || i.warning || 'OK' : i.creditNote}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

interface IBulkCreditNotesDrawer {
  isOpened: boolean;
  handleClose: () => void;
}

const BulkCreditNotesDrawer: FC<IBulkCreditNotesDrawer> = ({ isOpened, handleClose }) => {
  const { bulkCreateCreditNotes } = useClaims();

  const [rawNumbers, setRawNumbers] = useState<string>('');
  const [issued, setIssued] = useState<Date | null>(new Date());
  const [preview, setPreview] = useState<BulkCreditNoteResult>();
  const [result, setResult] = useState<BulkCreditNoteResult>();
  const [confirming, setConfirming] = useState<boolean>(false);
  const [working, setWorking] = useState<boolean>(false);

  const invoiceNumbers = parseInvoiceNumbers(rawNumbers);
  const isValidDate = !!issued && !Number.isNaN(issued.getTime());

  const resetChecks = () => {
    setPreview(undefined);
    setResult(undefined);
    setConfirming(false);
  };

  const handleCheck = async () => {
    setWorking(true);
    resetChecks();
    setPreview(await bulkCreateCreditNotes(invoiceNumbers, issued, true));
    setWorking(false);
  };

  const handleCreate = async () => {
    setWorking(true);
    const res = await bulkCreateCreditNotes(invoiceNumbers, issued, false);
    setWorking(false);
    setConfirming(false);
    if (res) {
      setPreview(undefined);
      setResult(res);
    }
  };

  const handleCloseAndReset = () => {
    resetChecks();
    setRawNumbers('');
    setIssued(new Date());
    handleClose();
  };

  return (
    <RightDrawer isOpened={isOpened} handleClose={handleCloseAndReset} size="half">
      <Box sx={{ m: 3 }}>
        <Stack direction="column" spacing={3}>
          <Typography variant="h5">Hromadné dobropisy</Typography>
          <Typography variant="body2" color="text.secondary">
            Vlož čísla faktúr (každé na nový riadok, alebo oddelené čiarkou/medzerou). Najprv sa
            skontroluje, ku ktorým sa dá dobropis vystaviť, až potom sa vystavia. Pohľadávky sa
            zároveň označia ako zrušené.
          </Typography>
          <TextField
            label="Čísla faktúr"
            multiline
            minRows={6}
            value={rawNumbers}
            onChange={(e) => {
              setRawNumbers(e.target.value);
              resetChecks();
            }}
            helperText={`Rozpoznaných čísel: ${invoiceNumbers.length}`}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Dátum dobropisov"
              value={issued}
              onChange={(value) => {
                setIssued(value ? new Date(value) : null);
                resetChecks();
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <Stack direction="row-reverse">
            <Button
              variant="outlined"
              onClick={handleCheck}
              disabled={working || invoiceNumbers.length === 0 || !isValidDate}
            >
              Skontrolovať
            </Button>
          </Stack>

          {preview && (
            <>
              <Typography variant="h6">
                Pripravené na vystavenie ({preview.ready.length})
              </Typography>
              {preview.ready.length > 0 ? (
                <ItemsTable items={preview.ready} lastColumn="reason" />
              ) : (
                <Alert severity="info">Nie je k čomu vystaviť dobropis.</Alert>
              )}
              {preview.skipped.length > 0 && (
                <>
                  <Typography variant="h6">Preskočené ({preview.skipped.length})</Typography>
                  <ItemsTable items={preview.skipped} lastColumn="reason" />
                </>
              )}
              {preview.ready.length > 0 &&
                (confirming ? (
                  <Alert
                    severity="warning"
                    action={
                      <Stack direction="row" spacing={1}>
                        <Button
                          color="secondary"
                          variant="contained"
                          size="small"
                          disabled={working}
                          onClick={handleCreate}
                        >
                          Áno, vystaviť
                        </Button>
                        <Button
                          size="small"
                          disabled={working}
                          onClick={() => setConfirming(false)}
                        >
                          Späť
                        </Button>
                      </Stack>
                    }
                  >
                    Naozaj vystaviť {preview.ready.length} dobropisov s dátumom{' '}
                    {issued?.toLocaleDateString('sk-SK')}? Čísla dobropisov sa nedajú vrátiť.
                  </Alert>
                ) : (
                  <Stack direction="row-reverse">
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={working}
                      onClick={() => setConfirming(true)}
                    >
                      Vystaviť {preview.ready.length} dobropisov
                    </Button>
                  </Stack>
                ))}
            </>
          )}

          {result && (
            <>
              <Alert severity={result.skipped.length ? 'warning' : 'success'}>
                Vystavených dobropisov: {result.created.length}
                {result.skipped.length ? `, preskočených: ${result.skipped.length}` : ''}
              </Alert>
              {result.created.length > 0 && (
                <ItemsTable items={result.created} lastColumn="creditNote" />
              )}
              {result.skipped.length > 0 && (
                <>
                  <Typography variant="h6">Preskočené ({result.skipped.length})</Typography>
                  <ItemsTable items={result.skipped} lastColumn="reason" />
                </>
              )}
            </>
          )}
        </Stack>
      </Box>
    </RightDrawer>
  );
};

export default BulkCreditNotesDrawer;
